import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTicket, faSearch, faFilter, faCheckCircle, faClock, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';
import Swal from 'sweetalert2';
import { buildApiUrl, useBaseUrl } from '../../../utils/utils';

interface Ticket {
    id: number;
    subject: string;
    description: string;
    status: 'open' | 'in-progress' | 'resolved' | 'closed';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    category: 'technical' | 'billing' | 'general' | 'feature-request' | 'bug-report';
    created: string;
    updated: string;
}

const MyTickets: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [priorityFilter, setPriorityFilter] = useState<string>('all');
    const [clinicName, setClinicName] = useState<string>('Facility');
    const [viewing, setViewing] = useState<Ticket | null>(null);

    useEffect(() => {
        document.title = "My Support Tickets - Uzima Healthcare";
        localForage.getItem('clinic_name').then((n) => { if (n) setClinicName(n as string); });
        localForage.getItem('clinic_id').then((cid: any) => setClinicId(cid ? Number(cid) : null));
    }, []);

    const loadTickets = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ clinic_id: String(clinicId) });
            if (statusFilter !== 'all') params.set('status', statusFilter);
            if (priorityFilter !== 'all') params.set('priority', priorityFilter);
            const res = await fetch(buildApiUrl(baseUrl, `facilities/platform-tickets/?${params}`));
            const data = await res.json();
            if (data.status === 'success') setTickets(data.data || []);
        } catch (error) {
            console.error('Error loading tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTickets(); }, [clinicId, statusFilter, priorityFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const closeTicket = async (ticket: Ticket) => {
        const confirm = await Swal.fire({ title: 'Close this ticket?', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, close it' });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/platform-tickets/${ticket.id}/close`), { method: 'PATCH' });
            if (res.ok) {
                setViewing(null);
                loadTickets();
            }
        } catch { /* best-effort */ }
    };

    const filteredTickets = tickets.filter(ticket =>
        !searchQuery ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'open': return faTicket;
            case 'in-progress': return faSpinner;
            case 'resolved': return faCheckCircle;
            case 'closed': return faCheckCircle;
            default: return faTicket;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'open': return 'warning';
            case 'in-progress': return 'info';
            case 'resolved': return 'success';
            case 'closed': return 'secondary';
            default: return 'secondary';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'danger';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'success';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const statusCounts = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'open').length,
        inProgress: tickets.filter(t => t.status === 'in-progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length,
        closed: tickets.filter(t => t.status === 'closed').length
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />

            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faTicket} className="me-2 text-primary" />
                                    My Support Tickets
                                </h2>
                                <p className="text-muted mb-0">
                                    Track your support requests to the Uzima platform for {clinicName}
                                </p>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <h5 className="text-primary mb-1">{statusCounts.total}</h5>
                                        <small className="text-muted">Total</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <h5 className="text-warning mb-1">{statusCounts.open}</h5>
                                        <small className="text-muted">Open</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <h5 className="text-info mb-1">{statusCounts.inProgress}</h5>
                                        <small className="text-muted">In Progress</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <h5 className="text-success mb-1">{statusCounts.resolved}</h5>
                                        <small className="text-muted">Resolved</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <h5 className="text-secondary mb-1">{statusCounts.closed}</h5>
                                        <small className="text-muted">Closed</small>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <button className="btn btn-primary btn-sm" onClick={() => navigate('/institution/create-ticket')}>
                                            <FontAwesomeIcon icon={faTicket} className="me-1" />
                                            New Ticket
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search tickets..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon icon={faSearch} className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="all">All Status</option>
                                    <option value="open">Open</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                            <div className="col-lg-3">
                                <select className="form-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                                    <option value="all">All Priority</option>
                                    <option value="urgent">Urgent</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                            </div>
                        ) : (
                            <div className="row">
                                <div className="col-12">
                                    {filteredTickets.map((ticket) => (
                                        <div key={ticket.id} className="card border-0 shadow-sm mb-3" style={{ cursor: 'pointer' }} onClick={() => setViewing(ticket)}>
                                            <div className="card-body">
                                                <div className="row align-items-center">
                                                    <div className="col-lg-8">
                                                        <div className="d-flex align-items-start">
                                                            <div className="flex-shrink-0 me-3">
                                                                <FontAwesomeIcon icon={getStatusIcon(ticket.status)} className={`text-${getStatusColor(ticket.status)} fs-4`} />
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <h6 className="mb-0">{ticket.subject}</h6>
                                                                    <span className="badge bg-secondary">#{ticket.id}</span>
                                                                </div>
                                                                <p className="text-muted small mb-2">
                                                                    {ticket.description.length > 100 ? `${ticket.description.substring(0, 100)}...` : ticket.description}
                                                                </p>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <span className={`badge bg-${getStatusColor(ticket.status)}`}>{ticket.status.replace('-', ' ').toUpperCase()}</span>
                                                                    <span className={`badge bg-${getPriorityColor(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                                                                    <span className="badge bg-primary">{ticket.category.replace('-', ' ').toUpperCase()}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-4">
                                                        <div className="text-lg-end">
                                                            <div className="mb-2">
                                                                <small className="text-muted"><FontAwesomeIcon icon={faClock} className="me-1" />Created: {formatDate(ticket.created)}</small>
                                                            </div>
                                                            <div>
                                                                <small className="text-muted">Updated: {formatDate(ticket.updated)}</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!loading && filteredTickets.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faTicket} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No tickets found</h5>
                                <p className="text-muted">
                                    {tickets.length === 0
                                        ? "You haven't submitted any support tickets yet."
                                        : 'Try adjusting your search terms or filters'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <DashboardFooter />

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">#{viewing.id} — {viewing.subject}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Category:</strong> {viewing.category.replace('-', ' ')}</p>
                                <p><strong>Priority:</strong> <span className={`badge bg-${getPriorityColor(viewing.priority)}`}>{viewing.priority}</span></p>
                                <p><strong>Status:</strong> <span className={`badge bg-${getStatusColor(viewing.status)}`}>{viewing.status}</span></p>
                                <hr />
                                <p style={{ whiteSpace: 'pre-wrap' }}>{viewing.description}</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                {viewing.status !== 'resolved' && viewing.status !== 'closed' && (
                                    <button className="btn btn-danger" onClick={() => closeTicket(viewing)}>Withdraw Ticket</button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MyTickets;
